import type { ItemType, PriorityRule, RulesPatch, UserRules } from '@hugo/core'
import { defaultRules } from '@hugo/core'
import { ESTIMATE_CHOICES } from './plan'

/** Types of work by the names the details sheet and Settings give them. */
export const TYPE_LABELS: Record<ItemType, string> = {
  review: 'Review',
  reply: 'Reply',
  deliverable: 'Deliverable',
  decision: 'Decision',
  meeting: 'Meeting',
  admin: 'Admin',
  fix: 'Fix',
  other: 'Other',
}

/** The rows of "Time needed by type", in the order people think of them, untyped last. */
export const ESTIMATE_RULES: readonly { key: keyof UserRules['estimateMinutes']; label: string }[] =
  [
    ...(
      ['reply', 'decision', 'admin', 'review', 'meeting', 'fix', 'deliverable', 'other'] as const
    ).map((key) => ({ key, label: TYPE_LABELS[key] })),
    { key: 'untyped', label: 'No type' },
  ]

/** The choices a rule offers, always including the value it has now (e.g. set over MCP). */
export function choicesFor(choices: readonly number[], current: number): number[] {
  return [...new Set([...choices, current])].sort((a, b) => a - b)
}

export const RULE_CHOICES = {
  estimateMinutes: ESTIMATE_CHOICES,
  reminderIntervalWorkingDays: [1, 2, 3, 4, 5, 7, 10, 15],
  awaySoonDays: [0, 3, 7, 14, 21, 30],
  timeOffNoticeDays: [3, 7, 14, 21, 30, 45, 60],
  undatedNoticeWorkingDays: [1, 2, 3, 5, 10],
  focusBlockMinutes: [15, 30, 45, 60, 90, 120, 180, 240],
} as const

/** Whether the rules differ from hugo's defaults (offers "Back to hugo's defaults"). */
export function isCustom(rules: UserRules): boolean {
  return JSON.stringify(rules) !== JSON.stringify(defaultRules)
}

/** The change that puts every rule back to hugo's default. */
export const DEFAULTS_PATCH: RulesPatch = defaultRules

/** "#client", "Anna Keller", "Admin", "Due within 2 days": what a priority rule looks at. */
export function conditionLabel(when: PriorityRule['when']): string {
  switch (when.kind) {
    case 'tag':
      return `#${when.tag}`
    case 'person':
      return when.name
    case 'type':
      return TYPE_LABELS[when.type]
    case 'due_within':
      return when.days === 0
        ? 'Due today'
        : when.days === 1
          ? 'Due within 1 day'
          : `Due within ${when.days} days`
  }
}

/** "#client → High". */
export function ruleLabel(rule: PriorityRule): string {
  return `${conditionLabel(rule.when)} → ${rule.priority === 'high' ? 'High' : 'Low'}`
}

/** The rule at `index` in the user's list, if it is (still) there. */
export function ruleAt(rules: UserRules | undefined, index: number | null): PriorityRule | null {
  return index === null ? null : (rules?.priorityRules[index] ?? null)
}

/** The list after moving one rule up (-1) or down (+1); the first that matches wins. */
export function moveRule(
  rules: readonly PriorityRule[],
  index: number,
  by: -1 | 1,
): PriorityRule[] {
  const to = index + by
  if (to < 0 || to >= rules.length) return [...rules]
  const next = [...rules]
  const [rule] = next.splice(index, 1)
  if (rule) next.splice(to, 0, rule)
  return next
}
